import { useEffect, useState, type ReactNode } from 'react';
import { GivingContext } from './giving-context';
import { loadDraft, validId, type Draft, type Gift, type Currency } from './giving';
import { accountRequest, currentSession, reloadProfile, useSession } from './session';
import { DonorError, locked, readStored, storeRequired } from './http';
import { useLanguage } from '@/i18n/language-context';
import { ReceiptContactProvider } from './receipt-contact';

export function GivingProvider({ children }: { children: ReactNode }): JSX.Element {
  const session = useSession();
  const { locale } = useLanguage();
  const draftKey = `giving.draft:${session.profile?.user.id ?? 'guest'}`;
  const [loaded, setLoaded] = useState<{ key: string; draft: Draft }>(() => ({
    key: draftKey,
    draft: loadDraft(draftKey),
  }));
  const [guestChurchId, setGuestChurchId] = useState<string | null>(() => {
    const saved = readStored<unknown>('giving.church', null);
    return validId(saved) ? saved : null;
  });
  const churchId = session.profile
    ? (session.profile.preferences.selectedChurchId ?? null)
    : guestChurchId;
  const draft = loaded.key === draftKey ? loaded.draft : loadDraft(draftKey);
  useEffect(() => {
    setLoaded({ key: draftKey, draft: loadDraft(draftKey) });
    const sync = (event: StorageEvent): void => {
      if (event.key === draftKey) setLoaded({ key: draftKey, draft: loadDraft(draftKey) });
      if (event.key === 'giving.church' || event.key === null) {
        const saved = readStored<unknown>('giving.church', null);
        setGuestChurchId(validId(saved) ? saved : null);
      }
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, [draftKey]);
  useEffect(() => {
    const clear = (): void => {
      // Keep pending-operation metadata, but clear editable data belonging to the old account.
      if (loaded.key !== 'giving.draft:guest') localStorage.removeItem(loaded.key);
    };
    window.addEventListener('giving:identity-changed', clear);
    return () => window.removeEventListener('giving:identity-changed', clear);
  }, [loaded.key]);
  const persist = (items: Gift[]): void => {
    if (`giving.draft:${currentSession().profile?.user.id ?? 'guest'}` !== draftKey)
      throw new DonorError(401, 'GIVING_ACTOR_CHANGED');
    if (
      items.some(
        (item) =>
          !validId(item.projectId) ||
          !validId(item.organizationId) ||
          !Number.isSafeInteger(item.amount) ||
          item.amount <= 0,
      ) ||
      (['RON', 'EUR', 'USD'] as Currency[]).some(
        (currency) => items.filter((item) => item.currency === currency).length > 50,
      )
    )
      throw new DonorError(400, 'INVALID_AMOUNT');
    const next = { revision: crypto.randomUUID(), items };
    storeRequired(draftKey, {
      revision: next.revision,
      savedAt: Date.now(),
      items: items.map(({ projectId, organizationId, amount, currency }) => ({
        projectId,
        organizationId,
        amount,
        currency,
      })),
    });
    setLoaded({ key: draftKey, draft: next });
  };
  const replace = async (items: Gift[]): Promise<void> => {
    await locked('drafts', () => {
      persist(items);
    });
  };
  const change = async (gift: Gift): Promise<void> => {
    if (gift.note && gift.note.length > 1000) throw new DonorError(400, 'REQUEST_FAILED');
    await locked('drafts', () => {
      const source = loadDraft(draftKey);
      const current = source.revision === draft.revision ? draft.items : source.items;
      const found = current.some((item) => item.projectId === gift.projectId);
      const items = current.flatMap((item) =>
        item.projectId === gift.projectId ? (gift.amount ? [gift] : []) : [item],
      );
      if (!found && gift.amount) items.push(gift);
      persist(items);
    });
  };
  return (
    <GivingContext.Provider
      value={{
        draft,
        draftKey,
        change,
        replace,
        churchId,
        selectChurch: async (id) => {
          if (id !== null && !validId(id)) throw new DonorError(400, 'REQUEST_FAILED');
          if (currentSession().epoch !== session.epoch || session.phase === 'restoring')
            throw new DonorError(401, 'GIVING_ACTOR_CHANGED');
          const userId = session.profile?.user.id;
          if (userId) {
            const saved = await accountRequest<{ selectedChurchId: string | null }>(
              '/me/preferences',
              locale,
              {
                method: 'PATCH',
                body: { selectedChurchId: id },
              },
              userId,
            );
            if (saved.selectedChurchId !== id) throw new DonorError(503, 'REQUEST_FAILED');
            if (currentSession().profile?.user.id !== userId)
              throw new DonorError(401, 'GIVING_ACTOR_CHANGED');
            await reloadProfile(locale);
            return;
          }
          storeRequired('giving.church', id);
          setGuestChurchId(id);
        },
      }}
    >
      <ReceiptContactProvider>{children}</ReceiptContactProvider>
    </GivingContext.Provider>
  );
}
